import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Button, DatePicker, Empty, Modal, Segmented, Space, Spin, Table, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileSearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import RespuestasF4 from './RespuestasF4';
import RespuestasCompra, { NpsChip } from './RespuestasCompra';
import { esTipoCompra } from './cuestionarioCompra';
import BarraLista, { BotonActualizar } from './BarraLista';
import { FctEstado } from '../../fct/react';
import { fct } from '../../fct/tokens/tokens';
import {
  EncuestaRegistrada, RespuestaEncuesta, getEncuestasRegistradas, getRespuestasEncuesta,
} from '../../api/cxPanelApi';
import VisibilidadF4Switch from './VisibilidadF4Switch';
import { BuscadorCliente, TipoFiltro, TipoTag } from './CampanaSemanaTab';
import {
  FILTRO_TODOS, FiltroTipo, ORDEN_TIPOS_REGISTRADAS, TIPO_DESCRIPCION, aIsoDia, coincideBusqueda, coincideTipo,
  contarTipos, esPreguntaNps, mensajeError, preguntaLegible, presetsRango, respuestaLegible,
} from './panelCx';

const { Text } = Typography;
const { RangePicker } = DatePicker;

/** Rango máximo que admite el backend (días entre «desde» y «hasta»). */
const MAX_DIAS_RANGO = 366;

type FiltroRespondida = 'todas' | 'si' | 'no';

const fecha = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD/MM/YYYY') : iso;
};

const valorFecha = (iso?: string | null): number => (iso ? dayjs(iso).valueOf() : 0);

/** Estado de la encuesta: respondida (con su fecha) o sin respuesta. */
const EstadoTag: React.FC<{ fila: EncuestaRegistrada }> = ({ fila }) => (fila.respondida
  ? <FctEstado personalizado={{ etiqueta: `Respondida${fila.fechaRespuesta ? ` ${fecha(fila.fechaRespuesta)}` : ''}`, familia: 'exito' }} />
  : <FctEstado personalizado={{ etiqueta: 'Sin respuesta', familia: 'neutro' }} />);

/** Un dato de la cabecera de la ficha (etiqueta pequeña encima, valor debajo). */
const Dato: React.FC<{ etiqueta: string; children: React.ReactNode }> = ({ etiqueta, children }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: fct.tinta['500'] }}>{etiqueta}</div>
    <div style={{ color: fct.tinta['900'], fontWeight: 500 }}>{children}</div>
  </div>
);

/** Una respuesta en la ficha: sí/no como estado, el NPS como chip de color, puntuaciones como número grande y textos como cita. */
const ValorRespuesta: React.FC<{ valor: string; nps?: boolean }> = ({ valor, nps }) => {
  if (nps && /^\d{1,2}$/.test(valor.trim())) return <NpsChip valor={valor} />;
  if (valor === 'Sí') return <FctEstado personalizado={{ etiqueta: 'Sí', familia: 'exito' }} />;
  if (valor === 'No') return <FctEstado personalizado={{ etiqueta: 'No', familia: 'neutro' }} />;
  if (/^\d{1,2}$/.test(valor.trim())) {
    return <span style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.1, color: fct.marca['verde-oscuro'] }}>{valor.trim()}</span>;
  }
  return (
    <div style={{
      whiteSpace: 'pre-wrap', padding: '6px 12px', borderLeft: `3px solid ${fct.marca['verde-medio']}`,
      background: fct.superficie.hundida, borderRadius: 4,
    }}>
      {valor}
    </div>
  );
};

/** Ficha en ventana con la cabecera de la encuesta y sus respuestas, para leerlas de un vistazo (se cargan al abrirla). */
export const RespuestasModal: React.FC<{ encuesta: EncuestaRegistrada | null; onClose: () => void }> = ({ encuesta, onClose }) => {
  const [respuestas, setRespuestas] = useState<RespuestaEncuesta[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Descarta respuestas de peticiones viejas si se abre otra encuesta rápido. */
  const peticion = useRef(0);
  const id = encuesta?.idEncuestaRealizada;

  const cargar = useCallback(async () => {
    if (id == null) return;
    const n = ++peticion.current;
    setCargando(true);
    setError(null);
    setRespuestas([]);
    try {
      const datos = await getRespuestasEncuesta(id);
      if (n !== peticion.current) return;
      setRespuestas(datos);
    } catch (err) {
      if (n !== peticion.current) return;
      setError(mensajeError(err, 'las respuestas de la encuesta'));
    } finally {
      if (n === peticion.current) setCargando(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Modal
      open={!!encuesta}
      onCancel={onClose}
      footer={null}
      centered
      width="min(1180px, 94vw)"
      destroyOnClose
      title={encuesta ? (
        <Space size={8} wrap>
          <TipoTag tipo={encuesta.tipo} />
          <span>{encuesta.nombreEncuesta || TIPO_DESCRIPCION[encuesta.tipo] || 'Encuesta'}</span>
        </Space>
      ) : ''}
    >
      {encuesta && (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16,
            padding: '12px 16px', marginBottom: 20, background: fct.superficie.hundida, borderRadius: 6,
          }}>
            <Dato etiqueta="Cliente">
              {[encuesta.codigoCliente, encuesta.nombreCliente].filter(Boolean).join(' · ') || '—'}
              {encuesta.tipo === 'F4' && encuesta.rol && (
                <span style={{ marginInlineStart: 6, fontWeight: 400, color: fct.tinta['500'] }}>
                  ({encuesta.rol === 'Vendedor' ? 'parte vendedora' : 'parte compradora'})
                </span>
              )}
            </Dato>
            {/* SI: sobre qué farmacia preguntó; OC/ROC: quién la atendió. La farmacia sale también en la OC si la tiene. */}
            {(encuesta.tipo === 'SI' || encuesta.farmacia) && (
              <Dato etiqueta="Farmacia">{encuesta.farmacia || '—'}</Dato>
            )}
            {esTipoCompra(encuesta.tipo) && (
              <Dato etiqueta={encuesta.tipo === 'ROC' ? 'Asesor de la ROC' : 'Asesor'}>{encuesta.asesor || '—'}</Dato>
            )}
            {(!esTipoCompra(encuesta.tipo) || encuesta.ccl || encuesta.nombreOperacion) && (
              <Dato etiqueta="Operación">
                {[encuesta.ccl, encuesta.nombreOperacion].filter(Boolean).join(' · ') || '—'}
              </Dato>
            )}
            <Dato etiqueta="Fecha de la encuesta">{fecha(encuesta.fechaEncuesta)}</Dato>
            <Dato etiqueta="Estado"><EstadoTag fila={encuesta} /></Dato>
          </div>

          {error && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={error}
              action={<Button size="small" onClick={cargar}>Reintentar</Button>}
            />
          )}

          <Spin spinning={cargando} tip="Cargando respuestas…">
            <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingInlineEnd: 4, minHeight: 60 }}>
              {respuestas.length === 0 && !cargando && !error && (
                <Empty description="Esta encuesta no tiene respuestas registradas" />
              )}
              {/* F4: el mismo cuestionario que rellena Mystery (5 bloques); ROC/OC/SI: el formulario de Typeform que ve
                  el cliente, sin los códigos repetidos del sistema anterior; el resto, rejilla de tarjetas. */}
              {respuestas.length > 0 && encuesta.tipo === 'F4' && (
                <RespuestasF4 respuestas={respuestas} otras={(rs) => <Rejilla respuestas={rs} />} />
              )}
              {respuestas.length > 0 && esTipoCompra(encuesta.tipo) && (
                <RespuestasCompra tipo={encuesta.tipo} respuestas={respuestas} otras={(rs) => <Rejilla respuestas={rs} />} />
              )}
              {encuesta.tipo !== 'F4' && !esTipoCompra(encuesta.tipo) && <Rejilla respuestas={respuestas} />}
            </div>
          </Spin>
        </>
      )}
    </Modal>
  );
};

/** Rejilla de varias columnas: las respuestas cortas (nota, sí/no) van en tarjetas pequeñas y los comentarios
 *  largos ocupan la fila entera. */
const Rejilla: React.FC<{ respuestas: RespuestaEncuesta[] }> = ({ respuestas }) => (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
                {respuestas.map((r, i) => {
                  const respuesta = respuestaLegible(r.pregunta, r.respuesta);
                  const largo = respuesta != null && respuesta.trim().length > 40;
                  return (
                    <div
                      key={`${r.pregunta}-${i}`}
                      style={{
                        gridColumn: largo ? '1 / -1' : undefined, padding: '8px 12px', minWidth: 0,
                        border: `1px solid ${fct.linea.fila}`, borderRadius: 6, background: fct.superficie.tarjeta,
                      }}
                    >
                      {/* La pregunta de recomendación es el NPS: se llama así y su nota va en chip de color. */}
                      <div style={{ fontSize: 12, color: fct.tinta['500'], marginBottom: 4 }}>
                        {esPreguntaNps(r.pregunta) ? 'NPS' : preguntaLegible(r.pregunta)}
                      </div>
                      {respuesta == null
                        ? <Text type="secondary">Sin contestar</Text>
                        : <ValorRespuesta valor={respuesta} nps={esPreguntaNps(r.pregunta)} />}
                    </div>
                  );
                })}
              </div>
);

interface Props {
  /** Título y selector de vista: van al principio de la barra (una sola fila con los filtros). */
  cabecera: React.ReactNode;
  /** Texto del buscador (común a las vistas): nombre, código CL, CCL u operación. */
  busqueda: string;
  onBusqueda: (v: string) => void;
}

/**
 * «Registradas»: consulta de todas las encuestas registradas en el rango elegido (cualquier tipo),
 * con su estado y sus respuestas. Vista pasiva: las respuestas llegan solas desde el formulario,
 * aquí no se da de alta nada. Para las F4, además, el interruptor de visibilidad para Mystery.
 */
const RegistradasTab: React.FC<Props> = ({ cabecera, busqueda, onBusqueda }) => {
  const [rango, setRango] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(90, 'day').startOf('day'), dayjs().startOf('day')]);
  const [filas, setFilas] = useState<EncuestaRegistrada[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const peticion = useRef(0);
  const [respondida, setRespondida] = useState<FiltroRespondida>('todas');
  const [tipo, setTipo] = useState<FiltroTipo>(FILTRO_TODOS);
  const [abierta, setAbierta] = useState<EncuestaRegistrada | null>(null);

  const cargar = useCallback(async () => {
    const id = ++peticion.current;
    setLoading(true);
    setError(null);
    try {
      const datos = await getEncuestasRegistradas(aIsoDia(rango[0]), aIsoDia(rango[1]));
      if (id !== peticion.current) return;
      setFilas(datos);
    } catch (err) {
      if (id !== peticion.current) return;
      setFilas([]);
      setError(mensajeError(err, 'las encuestas registradas'));
    } finally {
      if (id === peticion.current) setLoading(false);
    }
  }, [rango]);

  useEffect(() => { cargar(); }, [cargar]);

  // Contadores sobre lo que pasa buscador + estado; la tabla, además, por el tipo elegido.
  const buscadas = useMemo(
    () => filas.filter((f) => (respondida === 'todas' || f.respondida === (respondida === 'si'))
      && coincideBusqueda(busqueda, f.nombreCliente, f.codigoCliente, f.ccl, f.nombreOperacion, f.asesor, f.farmacia)),
    [filas, respondida, busqueda],
  );
  const conteos = useMemo(() => contarTipos(buscadas), [buscadas]);
  const tiposPresentes = useMemo(() => {
    const hay = new Set(filas.map((f) => f.tipo));
    return ORDEN_TIPOS_REGISTRADAS.filter((t) => hay.has(t));
  }, [filas]);
  const visibles = useMemo(() => buscadas.filter((f) => coincideTipo(tipo, f.tipo)), [buscadas, tipo]);
  const filtrando = visibles.length !== filas.length;

  const columnas: ColumnsType<EncuestaRegistrada> = [
    {
      title: 'Fecha',
      dataIndex: 'fechaEncuesta',
      width: 105,
      render: (v?: string | null) => fecha(v),
      sorter: (a, b) => valorFecha(a.fechaEncuesta) - valorFecha(b.fechaEncuesta),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      width: 85,
      render: (v: string, f) => <TipoTag tipo={v} titulo={f.nombreEncuesta || undefined} />,
    },
    {
      title: 'CL',
      dataIndex: 'codigoCliente',
      width: 110,
      render: (v?: string | null, f?: EncuestaRegistrada) => v || <Text type="secondary">#{f?.idCliente}</Text>,
    },
    {
      title: 'CCL',
      dataIndex: 'ccl',
      width: 150,
      render: (v?: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Operación',
      dataIndex: 'nombreOperacion',
      width: 190,
      ellipsis: true,
      // Sin operación en la fila (SI y OC del sistema anterior), la farmacia de la solicitud.
      render: (v: string | null | undefined, f: EncuestaRegistrada) => {
        if (v) return v;
        if (f.farmacia) return <Tooltip title="Farmacia de la solicitud">{f.farmacia}</Tooltip>;
        return <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Cliente',
      dataIndex: 'nombreCliente',
      ellipsis: true,
      render: (v: string | null | undefined, fila: EncuestaRegistrada) => (
        <span>
          {/* En F4, la parte del cliente en la operación: PV vendedora, PC compradora. */}
          {fila.tipo === 'F4' && fila.rol && (
            <Tooltip title={fila.rol === 'Vendedor' ? 'Parte vendedora' : 'Parte compradora'}>
              {/* PV en verde de marca y PC en azul del isotipo: se distinguen de un vistazo (manual, sin
                  colores de Ant Design). */}
              <span style={{
                display: 'inline-block', marginInlineEnd: 6, padding: '0 6px', borderRadius: 4, fontSize: 12,
                fontWeight: 600, lineHeight: '18px', color: fct.tinta['900'],
                background: fila.rol === 'Vendedor' ? fct.marca['verde-tenue'] : fct.isotipo['6'],
                border: `1px solid ${fila.rol === 'Vendedor' ? fct.marca['verde-medio'] : fct.isotipo['4']}`,
              }}>
                {fila.rol === 'Vendedor' ? 'PV' : 'PC'}
              </span>
            </Tooltip>
          )}
          {v || <Text type="secondary">Sin nombre</Text>}
        </span>
      ),
    },
    {
      // OC/SI: responsable de la oportunidad; ROC: quien hizo la reunión ROC.
      title: 'Asesor',
      dataIndex: 'asesor',
      width: 240,
      ellipsis: { showTitle: true },
      render: (v?: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      // La nota de recomendación, con el mismo chip que en las respuestas (9-10 verde, 7-8 amarillo, 0-6 rojo).
      title: 'NPS',
      dataIndex: 'nps',
      width: 150,
      sorter: (a, b) => (a.nps ?? -1) - (b.nps ?? -1),
      render: (v?: number | null) => (v == null ? <Text type="secondary">—</Text> : <NpsChip valor={String(v)} />),
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 175,
      render: (_: unknown, f) => <EstadoTag fila={f} />,
    },
    {
      title: 'Visible para Mystery',
      key: 'visibleMystery',
      width: 160,
      align: 'center',
      render: (_: unknown, f) => {
        // Solo las F4 tienen visibilidad para Mystery; el resto no aplica.
        if (f.tipo !== 'F4' || f.visibleMystery == null) return <Text type="secondary">—</Text>;
        return (
          <VisibilidadF4Switch
            id={f.idEncuestaRealizada}
            visible={!!f.visibleMystery}
            quitadaManual={f.quitadaManual}
            onCambiado={(visible) => setFilas((fs) => fs.map((x) => (x.idEncuestaRealizada === f.idEncuestaRealizada
              // Mismo efecto que el SP de visibilidad (008): quitar marca «manual»; poner la limpia.
              ? { ...x, visibleMystery: visible, quitadaManual: !visible }
              : x)))}
          />
        );
      },
    },
    {
      title: 'Respuestas',
      key: 'acciones',
      width: 100,
      align: 'center',
      render: (_: unknown, f) => {
        const sin = f.numRespuestas === 0;
        return (
          <Tooltip title={sin ? 'Sin respuestas registradas' : `Ver respuestas (${f.numRespuestas})`}>
            {/* El span deja ver el tooltip aunque el botón esté deshabilitado. */}
            <span>
              <Button
                size="small"
                icon={<FileSearchOutlined />}
                disabled={sin}
                onClick={() => setAbierta(f)}
                aria-label="Ver respuestas"
              />
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <BarraLista
        cabecera={cabecera}
        filtros={
          <>
            <TipoFiltro tipos={tiposPresentes} value={tipo} onChange={setTipo} conteos={conteos} total={buscadas.length} />
            <Segmented<FiltroRespondida>
              value={respondida}
              onChange={setRespondida}
              options={[
                { label: 'Todas', value: 'todas' },
                { label: 'Respondidas', value: 'si' },
                { label: 'Sin respuesta', value: 'no' },
              ]}
            />
            <RangePicker
              format="DD/MM/YYYY"
              allowClear={false}
              value={rango}
              onChange={(v) => {
                if (v?.[0] && v?.[1]) setRango([v[0].startOf('day'), v[1].startOf('day')]);
              }}
              // Nada en el futuro y como mucho un año entre las dos fechas (límite del backend).
              disabledDate={(d, info) => d.isAfter(dayjs(), 'day')
                || (!!info?.from && Math.abs(d.startOf('day').diff(info.from.startOf('day'), 'day')) > MAX_DIAS_RANGO)}
              // Atajos: un clic en vez de dos fechas a mano.
              presets={presetsRango()}
              style={{ width: 230 }}
            />
          </>
        }
      >
        <BuscadorCliente value={busqueda} onChange={onBusqueda} placeholder="Buscar: nombre, CL, CCL, operación o asesor" />
        <BotonActualizar onClick={cargar} loading={loading} />
      </BarraLista>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error}
          action={<Button size="small" onClick={cargar}>Reintentar</Button>}
        />
      )}

      <Table<EncuestaRegistrada>
        rowKey="idEncuestaRealizada"
        columns={columnas}
        dataSource={visibles}
        loading={loading}
        size="small"
// Con la tabla vacía, sin barra de desplazamiento horizontal (se veía una barra suelta bajo «No hay…»).
        scroll={visibles.length ? { x: 1640 } : undefined}
        locale={{
          emptyText: error
            ? <Empty description="No se han podido cargar las encuestas" />
            : filtrando
              ? <Empty description="Ninguna encuesta coincide con la búsqueda o los filtros" />
              : <Empty description="No hay encuestas registradas en estas fechas" />,
        }}
        pagination={{
          defaultPageSize: 50,
          pageSizeOptions: [25, 50, 100, 200],
          showSizeChanger: true,
          showTotal: (t) => (filtrando ? `${t} de ${filas.length} encuestas` : `${t} encuestas`),
        }}
      />

      <RespuestasModal encuesta={abierta} onClose={() => setAbierta(null)} />
    </>
  );
};

export default RegistradasTab;
